import React from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { initialsFor, MEMBER_ROLES, sortMembers } from './memberRoles';

/**
 * Pick people and say what each of them is doing.
 *
 * Shared by the cycle roster and the bet team, which are the same interaction
 * over different candidate pools: a roster draws from the whole organization, a
 * bet team draws only from its cycle's roster.
 *
 * `tagsByUser` is optional and carries what a person has actually shipped. It is
 * shown at the point of choosing rather than on a separate page, because "who has
 * touched billing before" is a question you have while you are staffing, not
 * afterwards.
 */
const MemberPicker = ({
  candidates = [],
  value = [],
  onChange,
  disabled = false,
  label = 'Add someone',
  placeholder = 'Search by name or email',
  emptyHint = 'Nobody yet.',
  noCandidatesHint = 'No one to choose from.',
  tagsByUser = null,
  // Who among the candidates is not on the parent cycle's roster yet. Flagged
  // rather than hidden, since picking them quietly adds them to the cycle.
  offRosterIds = null,
}) => {
  const offRoster = offRosterIds || new Set();
  const chosen = sortMembers(value);
  const chosenIds = new Set(value.map(member => member.userId));

  /**
   * Fold a fresh dropdown selection back into the chosen list without disturbing
   * the roles already set. People still ticked keep the member object we already
   * have (and thus their role); newly ticked ones come in as plain members;
   * unticking drops them. The list below stays the source of truth for roles.
   */
  const reconcile = (selected) => {
    const selectedIds = new Set(selected.map(candidate => candidate.userId));
    const kept = value.filter(member => selectedIds.has(member.userId));
    const keptIds = new Set(kept.map(member => member.userId));
    const added = selected
      .filter(candidate => !keptIds.has(candidate.userId))
      .map(candidate => ({
        userId: candidate.userId,
        role: 'member',
        name: candidate.name,
        email: candidate.email,
        githubLogin: candidate.githubLogin || null,
      }));
    onChange([...kept, ...added]);
  };

  const setRole = (userId, role) => {
    onChange(value.map(member => (member.userId === userId ? { ...member, role } : member)));
  };

  const remove = (userId) => {
    onChange(value.filter(member => member.userId !== userId));
  };

  /**
   * A one-line hint about what this person has done, for the moment of choosing.
   *
   * Areas come first when they exist. A tag says what the work was for, which is
   * the question being asked while staffing; a repository name only says where the
   * code went, and answers it by proxy at best.
   */
  const summarizeTags = (userId) => {
    const person = tagsByUser?.[userId];
    if (person?.areas?.length) {
      const areas = person.areas.slice(0, 2).map(area => area.label).join(', ');
      return { prefix: 'worked on', text: areas };
    }
    if (person?.repos?.length) {
      // The two most active repositories; the full list lives on the People view.
      const repos = person.repos.slice(0, 2).map(repo => repo.name.split('/').pop()).join(', ');
      return { prefix: 'shipped in', text: repos };
    }
    return null;
  };

  return (
    <Box>
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        // All candidates, not just the unchosen ones, so already-picked people
        // stay in the list with their checkbox ticked and can be unticked.
        options={candidates}
        // Deliberately not disabled on an empty candidate list. A control that is
        // greyed out with a caption underneath reads as broken, and an empty list
        // here usually means the candidates are still loading, which resolves a
        // moment later. The empty case says so in noOptionsText instead, where
        // somebody who clicked will actually see it.
        disabled={disabled}
        value={candidates.filter(candidate => chosenIds.has(candidate.userId))}
        getOptionLabel={(option) => option.name || option.email || ''}
        isOptionEqualToValue={(option, val) => option.userId === val.userId}
        onChange={(event, selected) => reconcile(selected)}
        noOptionsText={candidates.length ? 'Everyone is already here' : noCandidatesHint}
        // The chosen people already render below with roles and remove buttons, so
        // chips in the input would only duplicate them.
        renderTags={() => null}
        renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props;
          const tagHint = summarizeTags(option.userId);
          return (
            <Box component="li" key={option.userId} {...rest}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                <Checkbox checked={selected} sx={{ p: 0.5 }} />
                <Avatar sx={{ width: 24, height: 24, fontSize: 11 }}>
                  {initialsFor(option.name)}
                </Avatar>
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Typography variant="body2" noWrap>{option.name || option.email}</Typography>
                  {tagHint && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {tagHint.prefix} {tagHint.text}
                    </Typography>
                  )}
                </Box>
                {offRoster.has(option.userId) && (
                  <Tooltip title="Not on this cycle yet. Picking them adds them to it.">
                    <Chip label="joins cycle" size="small" variant="outlined" sx={{ height: 18 }} />
                  </Tooltip>
                )}
                {option.orgRole === 'viewer' && (
                  <Chip label="viewer" size="small" variant="outlined" sx={{ height: 18 }} />
                )}
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField {...params} label={label} placeholder={placeholder} />
        )}
      />

      {chosen.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {emptyHint}
        </Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {chosen.map(member => (
            <Box
              key={member.userId}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
            >
              <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>
                {initialsFor(member.name)}
              </Avatar>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2" noWrap>
                  {member.name || member.email || 'Unknown'}
                </Typography>
                {!member.githubLogin && (
                  <Typography variant="caption" color="text.secondary">
                    No GitHub login set
                  </Typography>
                )}
              </Box>
              <TextField
                select
                size="small"
                value={member.role || 'member'}
                onChange={(e) => setRole(member.userId, e.target.value)}
                disabled={disabled}
                sx={{ width: 120, flexShrink: 0 }}
              >
                {MEMBER_ROLES.map(role => (
                  <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                ))}
              </TextField>
              <Tooltip title="Take them off">
                <IconButton
                  size="small"
                  onClick={() => remove(member.userId)}
                  disabled={disabled}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default MemberPicker;
